import type { ImageCrop } from '@pz/scene';
import { coverPhotoFrame, panPhotoCrop, photoPlacement, turnPhotoCrop, zoomPhotoCropAt } from '@pz/scene';
import { createLayer } from './layers.js';

/**
 * "Uredi sliku": the photo's framing on a screen of its own.
 *
 * On a phone a photo slot on the product preview can be a thumbnail — a face
 * on a keyring, a picture inside a heart on a mug — and pinching a 60-pixel
 * box is a guess. This opens the same slot as large as the screen allows, the
 * way every photo app's crop screen does: the part that prints is bright, the
 * rest of the picture is dimmed around it for context, one finger moves it,
 * two fingers (or the slider) zoom it, and the author's rotate step turns it.
 *
 * It writes nothing while open. "Gotovo" hands back the new framing (which the
 * canvas editor writes exactly as its own gestures do); closing any other way
 * leaves the photo as it was. The maths is `@pz/scene`'s — the renderer's own
 * framing — so what is framed here is what prints.
 */

export interface FramerOptions {
  /** Where the dialog lives: the widget's shadow root. */
  host: ShadowRoot | HTMLElement;
  /** The question's name, as the dialog's title. */
  title: string;
  /** The picture being framed (a cut-out's transparent PNG included). */
  imageUrl: string;
  /** Its natural pixel size. */
  source: { w: number; h: number };
  /** The slot's width over its height. */
  aspect: number;
  /** The framing it starts from (a cut-out's is `free`). */
  crop: ImageCrop;
  /** Degrees per tap of the rotate buttons; 0 hides them. */
  rotateStep: number;
  /** The picture is a cut-out: finer zoom steps, no dimmed surround. */
  cutout: boolean;
  /** The slot's mask as an alpha mask, and its clip shape (a heart, a circle). */
  maskUrl?: string;
  shapeMask?: string;
  /** The layer's colour effects, as CSS. */
  filter?: string;
  /** A mirrored layer shows its photo mirrored here too. */
  flipX?: boolean;
  flipY?: boolean;
  /** Zoom limits, the canvas editor's own. */
  clampZoom: (zoom: number, free: boolean) => number;
  /**
   * What the reset button goes back to. Absent: the framing the screen opened
   * with ("Vrati"). The collage board passes the slot's automatic framing, so
   * one tap undoes a hand framing ("Automatski").
   */
  resetCrop?: ImageCrop;
  /** The reset button's word; absent: "Vrati". */
  resetLabel?: string;
}

const TEXT = {
  hint: 'Prevuci da pomeriš · raširi prste da uvećaš',
  zoomIn: 'Uvećaj',
  zoomOut: 'Umanji',
  zoom: 'Veličina slike',
  turnLeft: 'Okreni ulevo',
  turnRight: 'Okreni udesno',
  reset: 'Vrati',
  cancel: 'Otkaži',
  done: 'Gotovo',
  close: 'Zatvori',
};

const icon = (paths: string): string =>
  `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
const ICONS = {
  minus: icon('<path d="M6 12h12"/>'),
  plus: icon('<path d="M12 6v12M6 12h12"/>'),
  turnLeft: icon('<path d="M4 5v5h5"/><path d="M5.6 15a7 7 0 1 0 1.4-7.4L4 10"/>'),
  turnRight: icon('<path d="M20 5v5h-5"/><path d="M18.4 15a7 7 0 1 1-1.4-7.4L20 10"/>'),
  reset: icon('<path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/>'),
  close: icon('<path d="M6 6l12 12M18 6L6 18"/>'),
};

/**
 * Where the reset button goes and what it says: the framing the screen opened
 * with and "Vrati", unless the caller names another (the collage board's
 * automatic framing, "Automatski").
 */
export function framerReset(options: Pick<FramerOptions, 'crop' | 'resetCrop' | 'resetLabel'>): { crop: ImageCrop; label: string } {
  return {
    crop: { ...(options.resetCrop ?? options.crop) },
    label: options.resetLabel || TEXT.reset,
  };
}

/** Resolves with the new framing on "Gotovo", undefined when left unchanged. */
export function openPhotoFramer(options: FramerOptions): Promise<ImageCrop | undefined> {
  return new Promise((resolve) => {
    const { source, aspect, clampZoom } = options;
    const free = options.crop.free === true;
    const { crop: start, label: resetLabel } = framerReset(options);
    let crop: ImageCrop = { ...options.crop };
    /** The framing a run of turns started from (see `turnPhotoCrop`). */
    let turnFrom: ImageCrop | undefined;
    let result: ImageCrop | undefined;

    const card = document.createElement('div');
    card.className = 'pz-framer-card';

    const head = document.createElement('div');
    head.className = 'pz-framer-head';
    const title = document.createElement('strong');
    title.textContent = options.title;
    const close = button(ICONS.close, TEXT.close, 'pz-framer-x');
    head.append(title, close);

    // The stage takes every finger: a pinch may start well outside a small
    // slot's box, which is the point of this screen.
    const stage = document.createElement('div');
    stage.className = 'pz-framer-stage';
    const frame = document.createElement('div');
    frame.className = 'pz-framer-box';
    if (options.flipX || options.flipY) frame.style.transform = `scale(${options.flipX ? -1 : 1}, ${options.flipY ? -1 : 1})`;
    const ghost = picture(options.imageUrl, 'pz-framer-ghost');
    const windowEl = document.createElement('span');
    windowEl.className = 'pz-framer-window';
    const shape = document.createElement('span');
    shape.className = 'pz-framer-window';
    const sharp = picture(options.imageUrl, 'pz-framer-pic');
    shape.appendChild(sharp);
    windowEl.appendChild(shape);
    setMask(windowEl, options.maskUrl);
    setMask(shape, options.shapeMask);
    const grid = document.createElement('span');
    grid.className = 'pz-framer-grid';
    grid.innerHTML = '<i></i><i></i><i></i><i></i>';
    // A cut-out's surround is transparent (or another person's head): nothing
    // there worth dimming in.
    if (options.cutout) ghost.hidden = true;
    frame.append(ghost, windowEl, grid);
    stage.appendChild(frame);

    const hint = document.createElement('p');
    hint.className = 'pz-framer-hint';
    hint.textContent = TEXT.hint;

    const zoomRow = document.createElement('div');
    zoomRow.className = 'pz-framer-zoom';
    const zoomOut = button(ICONS.minus, TEXT.zoomOut);
    const zoomIn = button(ICONS.plus, TEXT.zoomIn);
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = '1000';
    slider.step = '1';
    slider.className = 'pz-framer-slider';
    slider.setAttribute('aria-label', TEXT.zoom);
    zoomRow.append(zoomOut, slider, zoomIn);

    const tools = document.createElement('div');
    tools.className = 'pz-framer-tools';
    const turnLeft = button(ICONS.turnLeft, TEXT.turnLeft);
    const turnRight = button(ICONS.turnRight, TEXT.turnRight);
    const reset = button(ICONS.reset, resetLabel, 'pz-framer-reset', resetLabel);
    if (options.rotateStep > 0) tools.append(turnLeft, turnRight);
    tools.append(reset);

    const actions = document.createElement('div');
    actions.className = 'pz-framer-actions';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'pz-btn-ghost';
    cancel.textContent = TEXT.cancel;
    const done = document.createElement('button');
    done.type = 'button';
    done.className = 'pz-btn';
    done.textContent = TEXT.done;
    actions.append(cancel, done);

    card.append(head, stage, hint, zoomRow, tools, actions);

    const layer = createLayer({
      host: options.host,
      className: 'pz-framer',
      content: card,
      label: options.title,
      onClose: () => {
        window.removeEventListener('resize', layout);
        resolve(result);
        window.setTimeout(() => layer.destroy(), 0);
      },
    });

    // ── Geometry ─────────────────────────────────────────────────────────────
    let box = { w: 1, h: 1 };
    const minZoom = clampZoom(0, free);
    const maxZoom = clampZoom(1e6, free);
    const toSlider = (zoom: number): string =>
      String(Math.round((Math.log(zoom / minZoom) / Math.log(maxZoom / minZoom)) * 1000));
    const fromSlider = (value: number): number => minZoom * (maxZoom / minZoom) ** (value / 1000);

    /** The slot as big as the stage allows, at its own proportions. */
    function layout(): void {
      const room = stage.getBoundingClientRect();
      const pad = options.cutout ? 12 : 36;
      const w = Math.max(80, room.width - pad * 2);
      const h = Math.max(80, room.height - pad * 2);
      box = w / h > aspect ? { w: h * aspect, h } : { w, h: w / aspect };
      frame.style.width = `${box.w}px`;
      frame.style.height = `${box.h}px`;
      draw();
    }

    function draw(): void {
      const place = photoPlacement(source, box, crop);
      for (const img of [ghost, sharp]) {
        img.style.width = `${place.w}px`;
        img.style.height = `${place.h}px`;
        img.style.left = `${place.x}px`;
        img.style.top = `${place.y}px`;
        img.style.transformOrigin = `${box.w / 2 - place.x}px ${box.h / 2 - place.y}px`;
        img.style.transform = place.rotation ? `rotate(${place.rotation}deg)` : '';
        img.style.filter = options.filter ?? '';
      }
      // Only the chosen head of a group cut-out shows, as on the product.
      const keep = crop.free ? crop.keep : undefined;
      sharp.style.clipPath = keep
        ? `inset(${keep.y * 100}% ${(1 - keep.x - keep.w) * 100}% ${(1 - keep.y - keep.h) * 100}% ${keep.x * 100}%)`
        : '';
      if (document.activeElement !== slider) slider.value = toSlider(crop.zoom);
      reset.disabled = sameCrop(crop, start);
    }

    /** Takes the renderer's clamped reading, keeping a cut-out's own flags. */
    const settle = (next: ImageCrop): ImageCrop => {
      const clamped = coverPhotoFrame(source, box, next).crop;
      return {
        ...clamped,
        ...(crop.free ? { free: true as const, ...(crop.keep ? { keep: crop.keep } : {}) } : {}),
      };
    };

    /** Zooms about a point of the box (its centre by default). */
    const zoomTo = (zoom: number, at?: { x: number; y: number }, to?: { x: number; y: number }, from = crop): void => {
      const next = clampZoom(zoom, free);
      const point = at ?? { x: box.w / 2, y: box.h / 2 };
      crop = settle(zoomPhotoCropAt(source, box, from, next, point, to ?? point));
      turnFrom = undefined;
      draw();
    };
    const step = options.cutout ? 1.05 : 1.12;
    repeatWhileHeld(zoomIn, () => zoomTo(crop.zoom * step));
    repeatWhileHeld(zoomOut, () => zoomTo(crop.zoom / step));
    slider.addEventListener('input', () => zoomTo(fromSlider(Number(slider.value))));

    const turn = (direction: -1 | 1): void => {
      const stepDeg = options.rotateStep;
      const current = crop.rotation ?? 0;
      let next = Math.round(current / stepDeg) * stepDeg + direction * stepDeg;
      next = (((next + 180) % 360) + 360) % 360 - 180;
      turnFrom ??= { ...crop };
      crop = settle(turnPhotoCrop(source, box, turnFrom, next));
      draw();
    };
    repeatWhileHeld(turnLeft, () => turn(-1));
    repeatWhileHeld(turnRight, () => turn(1));
    reset.addEventListener('click', () => {
      crop = { ...start };
      turnFrom = undefined;
      draw();
    });

    // ── Gestures ─────────────────────────────────────────────────────────────
    /** A viewport point in the box's own frame (unmirrored), in box pixels. */
    const toBox = (x: number, y: number): { x: number; y: number } => {
      const r = frame.getBoundingClientRect();
      const lx = x - r.left;
      const ly = y - r.top;
      return { x: options.flipX ? r.width - lx : lx, y: options.flipY ? r.height - ly : ly };
    };
    const pointers = new Map<number, { x: number; y: number }>();
    let pan: { x: number; y: number; crop: ImageCrop } | undefined;
    let pinch: { dist: number; crop: ImageCrop; mid: { x: number; y: number } } | undefined;
    const restart = (): void => {
      const [a, b] = [...pointers.values()];
      pan = undefined;
      pinch = undefined;
      if (a && b) pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), crop: { ...crop }, mid: toBox((a.x + b.x) / 2, (a.y + b.y) / 2) };
      else if (a) pan = { x: a.x, y: a.y, crop: { ...crop } };
    };
    stage.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      try {
        stage.setPointerCapture(event.pointerId);
      } catch {
        /* synthetic pointers have no capturable id */
      }
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      stage.classList.add('is-active');
      restart();
    });
    stage.addEventListener('pointermove', (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const [a, b] = [...pointers.values()];
      if (pinch && a && b) {
        if (pinch.dist <= 0) return;
        const ratio = Math.hypot(a.x - b.x, a.y - b.y) / pinch.dist;
        zoomTo(pinch.crop.zoom * ratio, pinch.mid, toBox((a.x + b.x) / 2, (a.y + b.y) / 2), pinch.crop);
        return;
      }
      if (pan) {
        crop = settle(panPhotoCrop(source, box, pan.crop, event.clientX - pan.x, event.clientY - pan.y, options.flipX, options.flipY));
        turnFrom = undefined;
        draw();
      }
    });
    const lift = (event: PointerEvent): void => {
      if (!pointers.delete(event.pointerId)) return;
      if (pointers.size === 0) stage.classList.remove('is-active');
      restart();
    };
    stage.addEventListener('pointerup', lift);
    stage.addEventListener('pointercancel', lift);
    stage.addEventListener('lostpointercapture', lift);
    stage.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        zoomTo(crop.zoom * Math.exp(-event.deltaY * (options.cutout ? 0.0006 : 0.0012)), toBox(event.clientX, event.clientY));
      },
      { passive: false },
    );
    // iOS Safari's own page zoom would take the pinch otherwise.
    for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
      stage.addEventListener(type, (event) => event.cancelable && event.preventDefault(), { passive: false });
    }
    // Arrow keys nudge, + and − zoom: the stage is focusable for keyboards.
    stage.tabIndex = 0;
    stage.setAttribute('aria-label', TEXT.hint);
    stage.addEventListener('keydown', (event) => {
      const nudge = event.shiftKey ? 20 : 4;
      const moves: Record<string, [number, number]> = { ArrowLeft: [nudge, 0], ArrowRight: [-nudge, 0], ArrowUp: [0, nudge], ArrowDown: [0, -nudge] };
      const move = moves[event.key];
      if (move) {
        event.preventDefault();
        crop = settle(panPhotoCrop(source, box, crop, -move[0], -move[1], options.flipX, options.flipY));
        turnFrom = undefined;
        draw();
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        zoomTo(crop.zoom * step);
      } else if (event.key === '-') {
        event.preventDefault();
        zoomTo(crop.zoom / step);
      }
    });

    close.addEventListener('click', () => layer.close());
    cancel.addEventListener('click', () => layer.close());
    done.addEventListener('click', () => {
      result = sameCrop(crop, options.crop) ? undefined : crop;
      layer.close();
    });

    layer.open();
    window.addEventListener('resize', layout);
    // Laid out once the dialog is on screen and has a size to measure.
    requestAnimationFrame(() => {
      layout();
      // A picture that was not loaded yet is drawn once it is.
      if (!sharp.complete) sharp.addEventListener('load', () => draw(), { once: true });
    });
  });
}

function picture(url: string, className: string): HTMLImageElement {
  const img = document.createElement('img');
  img.className = className;
  img.alt = '';
  img.draggable = false;
  img.src = url;
  return img;
}

function button(svg: string, label: string, className = '', text = ''): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = `pz-framer-btn ${className}`.trim();
  el.innerHTML = svg + (text ? `<span>${text}</span>` : '');
  el.title = label;
  el.setAttribute('aria-label', label);
  return el;
}

function setMask(element: HTMLElement, url: string | undefined): void {
  const value = url ? `url("${url.replace(/"/g, '%22')}")` : '';
  const style = element.style as CSSStyleDeclaration & { webkitMaskImage?: string };
  style.maskImage = value;
  style.webkitMaskImage = value;
}

function sameCrop(a: ImageCrop, b: ImageCrop): boolean {
  const r = (v: number | undefined): number => Math.round((v ?? 0) * 1000);
  return r(a.cx) === r(b.cx) && r(a.cy) === r(b.cy) && r(a.zoom) === r(b.zoom) && r(a.rotation) === r(b.rotation);
}

/**
 * A button that acts once per tap, and keeps acting while it is held — the
 * small zoom steps of a cut-out would otherwise take a dozen taps. Keyboard
 * activation (Enter, Space) arrives as a plain click and acts once.
 */
export function repeatWhileHeld(el: HTMLElement, act: () => void): void {
  let timer: number | undefined;
  let fromPointer = false;
  const stop = (): void => {
    window.clearTimeout(timer);
    window.clearInterval(timer);
    timer = undefined;
  };
  el.addEventListener('pointerdown', (event) => {
    if ((event as PointerEvent).button !== 0) return;
    fromPointer = true;
    act();
    stop();
    timer = window.setTimeout(() => {
      timer = window.setInterval(act, 70);
    }, 380);
  });
  for (const type of ['pointerup', 'pointerleave', 'pointercancel']) el.addEventListener(type, stop);
  el.addEventListener('click', () => {
    if (fromPointer) {
      fromPointer = false;
      return;
    }
    act();
  });
}
